import type { ConsumableReplenishment, WasteReplacement } from '@ctcells/contract'

import { orchestrator } from '../clients.ts'
import { keys } from '../keys.ts'
import { normalizeList, type Listed } from '../page.ts'
import { createQuery, queryParams } from '../query.ts'

import type { PageArgs } from './ops.ts'

/**
 * 기록 — 소모품 보충 이력 `GET /consumable-replenishments`(`IFC-RO-32` · `DM-REL-46`) ·
 * 폐기통 교체 이력 `GET /waste-replacements`(`IFC-RO-20` · `DM-REL-22`).
 *
 * 실물(`ctcells-core` `views/modules.py` `list_consumable_replenishments` · `views/maintenance.py` `list_waste_replacements` ·
 * core@d50b8cf4 · dev2 2026-10-12 실측 보충 172건 · 교체 80건)
 *
 * | 칸 | 값 |
 * | --- | --- |
 * | 역할 | **operator · admin**(`HISTORY_READ_ROLES` · 사람 귀속 칸이라 감사 접촉면) — viewer 403 |
 * | 응답 | `Page` — 최신순 · `limit`/`offset` |
 * | 거르기 | 보충 `module_id` · `kind`(`tip`·`tc`·`reagent` 밖이면 422) · 교체 `container_id` |
 * | 빈 칸 | 🔴 **`exclude_none`** — `count_after` · `reagent` · `replenished_by` · `attribution_basis` · `note` 가 없으면 **키째 없다**. `== null` 로 읽는다 |
 * | 사람 | `replenished_by` · `replaced_by` 는 **user_id(UUID)** 다. 이름은 `/users`(admin 한정) 대조로만 얻는다 |
 *
 * 🔴 viewer 면 부르지 않는다(`skip`) — 컨테이너가 접근으로 가른다. 주기 폴링 없다 — 스냅샷 1회 + 쓰기 뒤 무효화.
 */
export interface ReplenishmentListArgs extends PageArgs {
  module_id?: string
  kind?: ConsumableReplenishment['kind']
}

export interface WasteReplacementListArgs extends PageArgs {
  container_id?: string
}

export const useConsumableReplenishments = createQuery<
  ReplenishmentListArgs,
  Listed<ConsumableReplenishment>
>({
  key: (args) => keys.replenishments.list(queryParams(args)),
  fetch: async (args) =>
    normalizeList<ConsumableReplenishment>(
      await orchestrator.request({
        method: 'GET',
        path: '/consumable-replenishments',
        query: queryParams(args),
        retryOn401: true,
      }),
      args,
    ),
})

export const useWasteReplacements = createQuery<WasteReplacementListArgs, Listed<WasteReplacement>>(
  {
    key: (args) => keys.wasteReplacements(queryParams(args)),
    fetch: async (args) =>
      normalizeList<WasteReplacement>(
        await orchestrator.request({
          method: 'GET',
          path: '/waste-replacements',
          query: queryParams(args),
          retryOn401: true,
        }),
        args,
      ),
  },
)
